import type WaButton from "@awesome.me/webawesome/dist/components/button/button.js";
import {
  listeningReady,
  type HelpAction,
  type ItemHelp,
} from "../fuxi/yufa-practice.js";
import { listeningAddress } from "../content/lianxi/yufa-listening.js";
import { meaningBodyOf } from "../content/lianxi/yufa-meaning.js";
import { SentencePlayback, type PlaybackState } from "./sentence-playback.js";
import { yufaPart } from "./lianxi-yufa-item.js";
import { pointBack } from "./sign-in-link.js";
import {
  listenOffer,
  signUpLinkClass,
  signUpRoute,
} from "../user/sign-up-offer.js";

/**
 * What `head/session-state.html` writes on the document for a reader carrying
 * no session. Any other value, or none, plays, as in `audio-controls.ts`.
 */
const signedOut = "out";

/**
 * Listening controls reuse the exercise's saved assistance and answer lifecycle.
 *
 * **A signed-out reader is asked for an account instead.** Their press plays
 * nothing and the status line offers one, the pseudo-gate `audio-controls.ts`
 * puts on every other recording. The transcript stays, so a visitor can still
 * answer from the text. `docs/audio.md` under "Playing it asks for an account"
 * has why the audio is gated at all.
 */
export class YufaListening {
  readonly #player: SentencePlayback;
  readonly #play: WaButton;
  readonly #reveal: WaButton;
  readonly #status: HTMLElement;
  #help: ItemHelp = { hints: 0, answer: false };
  #failed = false;
  #state: PlaybackState | undefined;
  #returnFocus = false;

  constructor(
    private readonly form: HTMLFormElement,
    private readonly assist: (action: HelpAction) => void,
  ) {
    this.#play = yufaPart(form, "listen") as WaButton;
    this.#reveal = yufaPart(form, "transcript-button") as WaButton;
    this.#status = yufaPart(form, "listen-status")!;
    this.#player = new SentencePlayback({
      changed: (state): void => this.changed(state),
    });
    this.#play.addEventListener("click", () => {
      if (form.hidden !== false) {
        return;
      }
      if (
        form.ownerDocument.documentElement.dataset["cbSession"] === signedOut
      ) {
        this.offerAccount();
        return;
      }
      this.#returnFocus = form.ownerDocument.activeElement === this.#play;
      const body = meaningBodyOf(
        JSON.parse(
          form.querySelector<HTMLElement>("[data-cb-yufa-meaning]")?.dataset[
            "cbYufaMeaning"
          ] ?? "null",
        ) as unknown,
      );
      if (body?.listening === undefined) {
        this.changed("unavailable");
        return;
      }
      const voice = Math.random() < 0.5 ? "female" : "male";
      void this.#player.play(
        listeningAddress(body.listening, voice),
        body.listening.sha256[voice],
      );
    });
    this.#reveal.addEventListener("click", () => {
      if (form.hidden === false) {
        this.#player.stop();
        this.#state = undefined;
        this.assist(this.#failed ? "audioFallback" : "transcript");
        yufaPart(form, "transcript")?.focus();
      }
    });
  }

  /** An answer needs a complete play or an explicit switch to visible text. */
  ready(): boolean {
    return listeningReady(this.#help);
  }

  /** Apply kept exposure when a question resumes or another tab advances it. */
  show(help: ItemHelp): void {
    this.#help = help;
    const answered = this.form.dataset["cbYufaAnswered"] !== undefined;
    const transcript = yufaPart(this.form, "transcript")!;
    transcript.hidden =
      help.transcript !== true &&
      help.audioFallback !== true &&
      help.shown !== true &&
      !answered;
    this.#reveal.hidden = !transcript.hidden;
    this.#reveal.setAttribute("aria-expanded", String(!transcript.hidden));
    this.#play.disabled =
      this.#state === "loading" || this.#state === "playing";
    this.#play.textContent =
      help.listened === true ? "Replay sentence" : "Play sentence";
    const check = yufaPart(this.form, "check")!;
    check.toggleAttribute("disabled", !this.ready());
    if (answered) {
      this.#status.textContent = "Answer checked. You can replay the sentence.";
    } else if (help.audioFallback === true) {
      this.#status.textContent =
        "Read the sentence and choose its meaning. This answer uses the text.";
    } else if (help.transcript === true) {
      this.#status.textContent =
        "The transcript is showing. This answer uses the text.";
    } else if (this.#state === undefined && help.listened === true) {
      this.#status.textContent =
        "Choose the matching meaning. You can replay the sentence.";
    }
  }

  /** Keep hidden questions silent and return the play control to its idle state. */
  stop(): void {
    this.#player.stop();
    this.#state = undefined;
    this.#failed = false;
    this.#reveal.textContent = "Show transcript";
    this.#status.textContent =
      "Listen to the sentence, then choose the matching meaning.";
  }

  /** Place keyboard focus on the action needed before answering. */
  focus(): void {
    void this.#play.updateComplete.then(() => {
      if (this.form.hidden === false && !this.ready()) {
        this.#play.focus();
      }
    });
  }

  /** Put the account offer in the status line, where a press is answered. */
  private offerAccount(): void {
    const page = this.form.ownerDocument;
    const link = page.createElement("a");

    link.className = signUpLinkClass;
    link.href = signUpRoute;
    link.textContent = listenOffer;
    pointBack(link, page.location.pathname);
    this.#status.replaceChildren(link);
  }

  private changed(state: PlaybackState): void {
    this.#state = state;
    this.#failed = state === "unavailable";
    this.#play.disabled = state === "loading" || state === "playing";
    this.#reveal.textContent = this.#failed
      ? "Use text instead"
      : "Show transcript";
    const answered = this.form.dataset["cbYufaAnswered"] !== undefined;
    this.#status.textContent =
      state === "loading"
        ? "Loading the recording…"
        : state === "playing"
          ? "Playing the sentence…"
          : state === "ended"
            ? answered
              ? "Answer checked. You can replay the sentence."
              : "Choose the matching meaning. You can replay the sentence."
            : answered
              ? "The recording could not play. Your checked answer is unchanged."
              : "The recording could not play. Try again or use the text. No answer has been recorded.";
    if (state === "ended") {
      this.assist("listened");
    }
    if (state === "ended" || state === "unavailable") {
      void this.#play.updateComplete.then(() => {
        if (
          this.#returnFocus &&
          this.form.hidden === false &&
          this.form.ownerDocument.activeElement === this.form.ownerDocument.body
        ) {
          this.#play.focus();
        }
      });
    }
  }
}
